import React, { useEffect, useState } from "react";
import { Pencil, Plus, Power } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import Layout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import CardHeader from "src/components/common/card/CardHeader";
import PageHeader from "src/components/common/PageHeader";
import Button from "src/components/ui/Button";
import TableV2 from "src/components/ui/TableV2";
import { Badge } from "../../../components/ui/Badge";
import ConfirmationModal from "src/components/modals/ConfirmationModal";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as tenantActions from "src/redux/slices/tenantSlice";
import { formatDateTime } from "src/utils/date-time";
import { toast } from "react-toastify";
import { TenantPlanSnapshot } from "src/types/tenant.types";

const getActivePlan = (plans: TenantPlanSnapshot[] | undefined) => {
  if (!plans?.length) return null;
  const now = new Date();
  const sortedPlans = [...plans].sort((a, b) => {
    const aDate = a.planStartsAt || a.createdAt || "";
    const bDate = b.planStartsAt || b.createdAt || "";
    return new Date(bDate).getTime() - new Date(aDate).getTime();
  });
  return (
    sortedPlans.find((plan) =>
      plan.planEndsAt ? new Date(plan.planEndsAt) >= now : false
    ) || sortedPlans[0]
  );
};

const getPlanStatus = (planEndsAt?: string) => {
  if (!planEndsAt) return "active";
  return new Date(planEndsAt) < new Date() ? "expired" : "active";
};

const TenantListPage: React.FC = () => {
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusRow, setStatusRow] = useState<any | null>(null);
  const [limit, setLimit] = useState(10);

  const { editTenant, tenantList } = useAppSelector((state) => state.tenant);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    getTenantList(1);
  }, []);

  // ✅ Success/Error side-effects
  useEffect(() => {
    if (editTenant.data) {
      toast.success("Tenant modified successfully");
      dispatch(tenantActions.resetEditTenant());
      handleStatusModalClose();
      // onClose();
      getTenantList(1);
    } else if (editTenant.error) {
      toast.error(`Error: ${editTenant.error}`);
      handleStatusModalClose();
    }
  }, [editTenant]);

  const getTenantList = (page: number) =>
    dispatch(tenantActions.getTenantsListRequest({ page, limit }));

  const handleUserStatus = (userId: string, status: "active" | "suspended") => {
    const payload = {
      _id: userId,
      tenantStatus: status,
    };
    dispatch(tenantActions.editTenantRequest(payload));
  };

  const handleStatusModalClose = () => {
    setShowStatusModal(false);
    setStatusRow(null);
  };

  const onConfirm = () => {
    if (statusRow?._id) {
      handleUserStatus(
        statusRow?._id,
        statusRow?.tenantStatus === "active" ? "suspended" : "active"
      );
    }
  };

  const handlePageChange = (page: number) => {
    getTenantList(page);
  };

  return (
    <Layout
      type="admin"
      meta={{
        title: "Tenants | Invyntra",
        description:
          "Track and manage all tenant details with comprehensive information.",
        keywords: "tenants, details, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Header */}
        <PageHeader
          title="Tenants"
          breadcrumbData={[
            { label: "Home", path: "/admin/dashboard" },
            { label: "Tenants", path: "/admin/tenants/list" },
          ]}
        />

        <Card>
          <CardHeader
            title="Tenants"
            description="Manage all tenants here"
            actions={
              <Button onClick={() => navigate("/admin/tenant/create")}>
                <Plus size={16} className="mr-1" /> Add Tenant
              </Button>
            }
          />

          {/* Table */}
          <TableV2
            columns={[
              {
                key: "tenantId",
                header: "Transaction Id",
                accessor: ({ row, key }) =>
                  // <div className="w-32">#{row?.transactionId}</div>
                  row[key] ? (
                    <Link
                      className="underline"
                      to={`/admin/tenant/${row?._id}/details`}
                    >
                      #{row[key]}
                    </Link>
                  ) : (
                    "-"
                  ),
              },
              {
                key: "tenantName",
                header: "Tenant Name",
                width: "100px",
              },
              { key: "tenantEmail", header: "Email" },
              { key: "tenantAddress", header: "Address" },
              {
                key: "createdAt",
                header: "Joined On",
                accessor: ({ key, row }) => (
                  <div>{formatDateTime(row[key], "DD/MM/YYYY")}</div>
                ),
              },
              {
                key: "tenantStatus",
                header: "Status",
                accessor: ({ key, row }) => (
                  <Badge
                    text={row?.[key] || ""}
                    color={
                      row?.[key] === "active"
                        ? "green"
                        : row?.[key] === "draft"
                        ? "yellow"
                        : "red"
                    }
                  />
                ),
              },
              {
                key: "plans",
                header: "Plan Name",
                accessor: ({ row }) => {
                  const activePlan = getActivePlan(row?.plans);
                  const planLabel = activePlan?.planName;
                  const planCode = activePlan?.planCode || activePlan?.planId;

                  return planLabel || planCode;
                },
              },
              {
                key: "plans",
                header: "Plan Status",
                accessor: ({ row }) => {
                  const activePlan = getActivePlan(row?.plans);
                  const planStatus = getPlanStatus(activePlan?.planEndsAt);

                  return (
                    <Badge
                      text={planStatus}
                      color={planStatus === "active" ? "green" : "red"}
                    />
                  );
                },
              },
              {
                key: "actions",
                header: "Actions",
                accessor: ({ key, row }) => (
                  <div className="pr-2">
                    <button
                      className="p-2 rounded-md hover:bg-primary-100 text-primary-600"
                      onClick={() => navigate(`/admin/tenant/${row._id}/edit`)}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      className={`p-2 rounded-md ${
                        row.tenantStatus === "active"
                          ? "hover:bg-red-100 text-red-600"
                          : "hover:bg-green-100 text-green-600"
                      }`}
                      onClick={() => {
                        setStatusRow(row);
                        setShowStatusModal(true);
                      }}
                    >
                      <Power size={14} />
                    </button>
                  </div>
                ),
              },
            ]}
            data={tenantList?.data?.data}
            pagination={tenantList?.data?.pagination}
            onPageChange={handlePageChange}
          />
        </Card>
      </div>

      {showStatusModal && statusRow?._id && (
        <ConfirmationModal
          message={`Are you sure to ${
            statusRow?.tenantStatus === "active" ? "suspend" : "activate"
          } this tenant?`}
          onConfirm={onConfirm}
          onCancel={handleStatusModalClose}
          open={showStatusModal}
          isLoading={editTenant?.loading}
        />
      )}
    </Layout>
  );
};

export default TenantListPage;
